import moment from 'moment-timezone';
import { ISO_DATE_TIME_FORMAT } from '../../../../constants/constant';

export const unmaskPostalCode = (postalCode: string): string => {
  if (postalCode && postalCode !== '') {
    return postalCode?.replace(/\D/g, '');
  }
  return '';
};

export const maskPostalCode = (postalCode: string): string => {
  if (postalCode && postalCode !== '') {
    const cleanedPostalCode = postalCode?.replace(/\D/g, '');
    if (cleanedPostalCode?.length <= 5) {
      return cleanedPostalCode;
    } else {
      return `${cleanedPostalCode?.substring(0, 5)}-${cleanedPostalCode?.substring(5)}`;
    }
  }
  return '';
};

export const unmaskPhoneNumber = (phoneNumber: string): string => {
  if (phoneNumber && phoneNumber !== '') {
    return phoneNumber?.replace(/\D/g, '');
  }
  return '';
};

export const convertDateFormat = (inputDate: string, inputDateFormat: string, outputDateFormat: string) => {
  return moment(inputDate, inputDateFormat).format(outputDateFormat);
};

export const getCurrentCentralTime = (): string => {
  return moment().tz('America/Chicago').format(ISO_DATE_TIME_FORMAT);
};

export const getCurrentCSTHour = () => {
  const cstTime = getCurrentCentralTime();
  return Number(moment(cstTime).hour());
};

export const formatArrivalDate = (dateString: string, format: any) => {
  if (!dateString) {
    return '';
  }
  const date = moment(dateString, format.arrivalDateFormat);
  const currentYear = moment().year();
  const yearToUse =
    currentYear === date.year() ? format.arrivalDateFormatWithoutYear : format.arrivalDateFormatWithYear;
  return date.format(yearToUse);
};

export function maskPhoneNumber(phoneNumber: string, event?: any): string {
  let formattedPhoneNumber = '';
  if (phoneNumber && phoneNumber !== '') {
    const cleanedPhoneNumber = phoneNumber?.replace(/\D/g, '')?.slice(-10);
    formattedPhoneNumber = `(${cleanedPhoneNumber}`;
    if (cleanedPhoneNumber.length >= 6) {
      if (event && event?.nativeEvent?.inputType === 'deleteContentBackward' && cleanedPhoneNumber.length === 6) {
        formattedPhoneNumber = `(${cleanedPhoneNumber?.substring(0, 3)}) ${cleanedPhoneNumber?.substring(3, 6)}`;
      } else {
        formattedPhoneNumber = `(${cleanedPhoneNumber?.substring(0, 3)}) ${cleanedPhoneNumber?.substring(
          3,
          6,
        )}-${cleanedPhoneNumber?.substring(6)}`;
      }
    } else if (cleanedPhoneNumber.length >= 3) {
      if (event && event?.nativeEvent?.inputType === 'deleteContentBackward' && cleanedPhoneNumber.length === 3) {
        formattedPhoneNumber = `(${cleanedPhoneNumber?.substring(0, 3)}`;
      } else {
        formattedPhoneNumber = `(${cleanedPhoneNumber?.substring(0, 3)}) ${cleanedPhoneNumber?.substring(3, 6)}`;
      }
    }
  }
  return formattedPhoneNumber;
}

export const unmaskCardNumber = (maskedCardNumber: string): string => {
  if (maskedCardNumber && maskedCardNumber !== '') {
    return maskedCardNumber?.replace(/[\s-]/g, '');
  }
  return '';
};

export const formatExpirationYear = (expirationYear: string): string => {
  if (expirationYear && expirationYear !== '') {
    return expirationYear?.slice(-2);
  }
  return '';
};

export const formatDate = (dateString: string | undefined): string | undefined => {
  if (!dateString) return undefined;

  const dateObj = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', year: 'numeric' };
  const formattedDate = dateObj.toLocaleDateString('en-US', options);

  return formattedDate;
};

export const formatCreditDate = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const day = ('0' + date.getDate()).slice(-2);
  const year = date?.getFullYear()?.toString()?.slice(-2);
  return `${month}/${day}/${year}`;
};

export const formatDOB = (dob: string, oldDate: string): string => {
  if (oldDate?.length > dob?.length) {
    return dob;
  }
  const inputValue = dob?.replace(/\D/g, '').slice(0, 8);
  let formattedValue = '';

  if (inputValue.length <= 1) {
    formattedValue = parseInt(inputValue) > 1 ? `0${inputValue}/` : `${inputValue}`;
  } else if (inputValue.length < 4) {
    formattedValue =
      inputValue.length === 3 && parseInt(inputValue.slice(2)) > 3
        ? `${inputValue.slice(0, 2)}/0${inputValue.slice(2)}/`
        : `${inputValue.slice(0, 2)}/${inputValue.slice(2)}`;
  } else {
    formattedValue = `${inputValue.slice(0, 2)}/${inputValue.slice(2, 4)}/${inputValue.slice(4)}`;
  }
  return formattedValue;
};

export const maskCardNumber = (event: any, cardNumber: string): string => {
  const inputValue = cardNumber?.replace(/\D/g, '');
  let maskedValue = '';
  inputValue?.split('').map((char: any, index: any) => {
    if ((index + 1) % 4 === 0 && index + 1 <= 13) {
      maskedValue += char + ' ';
    } else {
      maskedValue += char;
    }
    return char;
  });

  if (
    event?.nativeEvent?.inputType === 'deleteContentBackward' &&
    maskedValue.length > 1 &&
    maskedValue[maskedValue?.length - 1] === ' '
  ) {
    maskedValue = maskedValue?.slice(0, -1);
  }
  return maskedValue;
};

export const Format = (formatString: string, args?: Array<string>) => {
  let formattedString = formatString;
  if (args) {
    formattedString = formatString.replace(/\{(\d+)\}/g, function (matcher) {
      const match = matcher.match(/(\d+)/g);
      if (match?.[0]) {
        const index = parseInt(match[0]);
        if (args.length > index) {
          return args[index];
        }
      }
      return matcher;
    });
  }
  return formattedString;
};

export function formatCurrency(amount: string, makeAmountPositive = false, showFreeText = false): string {
  if (!amount || amount === undefined) {
    return '$0.00';
  }
  const parsedAmount: number = parseFloat(amount);
  if (isNaN(parsedAmount)) {
    return '$0.00';
  }
  if (parsedAmount == 0) {
    if (showFreeText) {
      return 'FREE';
    } else {
      return '$0.00';
    }
  } else if (parsedAmount < 0) {
    if (makeAmountPositive) {
      return `$${(parsedAmount * -1)?.toFixed(2)}`;
    } else {
      return `-$${(parsedAmount * -1)?.toFixed(2)}`;
    }
  } else {
    return `$${parsedAmount.toFixed(2)}`;
  }
}

export function titleCase(string: string) {
  return string[0].toUpperCase() + string.slice(1).toLowerCase();
}

export function formatMaxOrderTotal(amount: number): string {
  return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function formatReviewCount(reviewCount: string): string {
  return `(${reviewCount?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')})`;
}

export function formatReviewsCount(reviewCount: string): string {
  return reviewCount?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const getParsedValue = (value: string | undefined) => {
  if (!value) return undefined;
  return Number(value);
};

export function stripLeadingZeroFromInput(value: string): string {
  return value.replace(/^(0+)\d/, (part) => Number(part)?.toString());
}

export const getPriceRangeDisplayName = (refinement: any) => {
  if (refinement?.length === 2) {
    return `$${refinement?.[0]?.value} - $${refinement?.[1]?.value}`;
  } else if (refinement?.length === 1 && refinement?.[0]?.operator === '<=') {
    return `$${refinement?.[0]?.value} & Below`;
  } else {
    return `$${refinement?.[0]?.value} & Above`;
  }
};

export const isValidRange = (
  min: number | undefined,
  max: number | undefined,
  appliedRange: { min: number; max: number },
) => {
  if (min && max && min >= max) {
    return false;
  } else if (min && min >= appliedRange.max) {
    return false;
  } else if (max && max > appliedRange.max) {
    return false;
  } else {
    return true;
  }
};

export function formatCurrencyWithCommas(value: string | number) {
  const numberValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numberValue)) {
    return '0.00';
  }
  const roundedValue = numberValue?.toFixed(2);
  // Format the number with commas (but preserve the decimals)
  const [integerPart, decimalPart] = roundedValue?.split('.');
  const formattedIntegerPart = new Intl.NumberFormat()?.format(parseInt(integerPart));
  const formattedValue = `${formattedIntegerPart}.${decimalPart}`;
  return formattedValue;
}
